import { defineStore } from 'pinia'
import api from '@/api/axios'
import { ref, computed } from 'vue'

export const useDataCollectionStore = defineStore('dataCollection', () => {
  // Core state
  const loading = ref(false)
  const error = ref(null)
  const collectionResults = ref(new Map()) // Store results by sessionId

  // Collection results state management
  function setCollectionResults(sessionId, data) {
    collectionResults.value.set(sessionId, data)
    console.log('Collection results stored for session:', sessionId, data)
  }

  function getCollectionResults(sessionId) {
    const results = collectionResults.value.get(sessionId)
    console.log('Getting collection results for session:', sessionId, results)
    return results
  }

  function clearCollectionResults(sessionId) {
    if (sessionId) {
      collectionResults.value.delete(sessionId)
    } else {
      collectionResults.value.clear()
    }
    console.log('Cleared collection results cache for session:', sessionId)
  }

  // Data collection categories
  async function getDataCollectionCategories(projectId, entityType) {
    if (!projectId) throw new Error('No project ID provided')
    loading.value = true
    error.value = null
    
    try {
      const response = await api.get(`/research/projects/${projectId}/data-collection/categories/${entityType}`)
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to get data collection categories'
      console.error('Categories API error:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        url: err.config?.url,
        data: err.response?.data
      })
      throw err
    } finally {
      loading.value = false
    }
  }

  // Start data collection
  async function startDataCollection(projectId, payload) {
    if (!projectId) throw new Error('Project ID is required')
    // Note: selectedCategories is now optional - API handles category selection server-side

    loading.value = true
    error.value = null

    try {
      console.log('Starting data collection with payload:', {
        projectId,
        sessionId: payload.sessionId,
        isUpdate: payload.isUpdate,
        collectionMode: payload.collectionMode
      })

      const requestPayload = {
        sessionId: payload.sessionId,
        options: {
          maxConcurrent: payload.maxConcurrent || 2,
          isUpdate: payload.isUpdate || false,
          collectionMode: payload.collectionMode || (payload.isUpdate ? 'selective' : 'initial')
        }
      }

      // Add selectedCategories only if provided (for backward compatibility)
      if (payload.selectedCategories && payload.selectedCategories.length > 0) {
        requestPayload.selectedCategories = payload.selectedCategories.map(category => ({
          id: category.categoryId || category.id,
          name: category.name || category.categoryId || category.id
        }))
      }

      // Add optional fields
      if (payload.customQuestions?.length) {
        requestPayload.customQuestions = payload.customQuestions
      }

      if (payload.entityContext) {
        requestPayload.entityContext = payload.entityContext
      }

      console.log('Making request to:', `/research/projects/${projectId}/data-collection/execute`)
      console.log('Request payload:', requestPayload)

      const response = await api.post(`/research/projects/${projectId}/data-collection/execute`, requestPayload)

      console.log('Data collection API response:', response.data)

      if (!response.data) {
        throw new Error('Invalid response: no data returned from server')
      }

      const responseData = response.data

      // Try multiple possible ID fields
      const collectionId = responseData.collectionId ||
        responseData.id ||
        responseData._id ||
        responseData.jobId ||
        responseData.sessionId ||
        responseData.taskId

      // Check for success indicators
      const isSuccessful = responseData.success === true ||
        responseData.status === 'started' ||
        responseData.status === 'running' ||
        responseData.status === 'queued' ||
        responseData.message?.toLowerCase().includes('started') ||
        responseData.message?.toLowerCase().includes('initiated') ||
        responseData.message?.toLowerCase().includes('queued')

      if (collectionId) {
        return {
          ...responseData,
          collectionId,
          isTemporaryId: false
        }
      } else if (isSuccessful) {
        const tempCollectionId = `temp_${payload.sessionId}_${Date.now()}`
        console.log('Success indicated without collectionId. Using temporary ID:', tempCollectionId)

        return {
          ...responseData,
          collectionId: tempCollectionId,
          isTemporaryId: true,
          originalResponse: responseData,
          sessionId: payload.sessionId,
          message: responseData.message || 'Collection started successfully'
        }
      } else {
        console.error('Unexpected API response structure:', {
          responseData,
          hasCollectionId: !!collectionId,
          hasSuccessIndicator: isSuccessful,
          responseKeys: Object.keys(responseData)
        })

        if (responseData.error) {
          throw new Error(`Collection failed: ${responseData.error}`)
        } else if (responseData.message) {
          const tempCollectionId = `info_${payload.sessionId}_${Date.now()}`
          return {
            ...responseData,
            collectionId: tempCollectionId,
            isTemporaryId: true,
            originalResponse: responseData,
            sessionId: payload.sessionId
          }
        } else {
          throw new Error(`Unexpected response format. Response: ${JSON.stringify(responseData)}`)
        }
      }

    } catch (err) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to start data collection'

      error.value = errorMessage

      console.error('Data collection start error:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
        url: err.config?.url,
        method: err.config?.method,
        projectId,
        sessionId: payload.sessionId
      })

      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  // Collection status methods
  async function getCollectionStatus(projectId, collectionId) {
    if (!projectId) throw new Error('Project ID is required')
    if (!collectionId) throw new Error('Collection ID is required')

    loading.value = true
    error.value = null

    try {
      // Handle temporary collection IDs
      if (collectionId.startsWith('temp_') || collectionId.startsWith('info_')) {
        console.log('Temporary collection ID detected, using session-based status check')
        const sessionId = collectionId.split('_')[1]
        if (sessionId) {
          return await getSessionCollectionStatus(projectId, sessionId)
        }
      }

      const response = await api.get(`/research/projects/${projectId}/data-collection/${collectionId}/status`)
      return response.data
    } catch (err) {
      // If the collection ID endpoint fails, try session-based approach
      if (collectionId.includes('_')) {
        const sessionId = collectionId.split('_')[1]
        if (sessionId) {
          console.log('Collection status failed, trying session-based status')
          try {
            return await getSessionCollectionStatus(projectId, sessionId)
          } catch (sessionErr) {
            console.error('Session-based status also failed:', sessionErr)
          }
        }
      }

      error.value = err.response?.data?.message || 'Failed to get collection status'
      console.error('Collection status error:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        url: err.config?.url,
        data: err.response?.data,
        collectionId
      })
      throw err
    } finally {
      loading.value = false
    }
  }

  async function getSessionCollectionStatus(projectId, sessionId) {
    try {
      const response = await api.get(`/research/projects/${projectId}/sessions/${sessionId}/data-collection/status`)

      const sessionData = response.data
      return {
        status: sessionData.status || 'running',
        progress: sessionData.progress || 0,
        completed: sessionData.completed || 0,
        total: sessionData.total || 1,
        currentActivity: sessionData.currentActivity || 'Processing...',
        estimatedTimeRemaining: sessionData.estimatedTimeRemaining || 0,
        categoryStatus: sessionData.categoryStatus || {},
        isSessionBased: true,
        sessionId
      }
    } catch (err) {
      console.error('Session collection status error:', err)
      throw err
    }
  }

  // Collection results with state-aware handling
  async function getLatestCollectionResults(projectId, sessionId, options = {}) {
    if (!projectId || !sessionId) {
      throw new Error('Project ID and Session ID are required')
    }

    loading.value = true
    error.value = null
    
    try {
      console.log('🔍 Data Collection Store: Fetching latest collection results from API:', { projectId, sessionId, options })
      
      const params = new URLSearchParams()
      if (options.includeArticles) {
        params.append('includeArticles', 'true')
      }
      if (options.limit) {
        params.append('limit', options.limit)
      }
      if (options.categoryId) {
        params.append('categoryId', options.categoryId)
      }
      
      const queryString = params.toString()
      const endpoint = `/research/projects/${projectId}/sessions/${sessionId}/data-collection/results${queryString ? `?${queryString}` : ''}`
      
      console.log('🔍 Data Collection Store: Making collection results request to:', endpoint)
      
      const response = await api.get(endpoint)
      const data = response.data
      
      console.log('🔍 Data Collection Store: Collection results response:', {
        endpoint,
        status: response.status,
        data: data,
        articlesData: data?.articles?.data?.length || 0,
        articlesPagination: data?.articles?.pagination,
        articleSummary: data?.articleSummary
      })
      
      if (!data) {
        throw new Error('No data returned from server')
      }
      
      setCollectionResults(sessionId, data)
      
      const processedResults = {
        ...data,
        sessionState: data.sessionState || 'unknown',
        nextSteps: data.nextSteps || [],
        articles: data.articles || { data: [], pagination: null },
        categoryBreakdown: data.categoryBreakdown || {},
        collectionMetadata: data.collectionMetadata || {},
        disambiguationStatus: data.disambiguationStatus || null,
        collectionProgress: data.collectionProgress || null,
        analysisCapabilities: data.analysisCapabilities || {},
        debug: data.debug || {},
        processingInfo: data.processingInfo || {},
        hasResults: Boolean(data.articles?.data?.length),
        hasCategories: Boolean(Object.keys(data.categoryBreakdown || {}).length),
        isComplete: data.sessionState === 'completed',
        needsDisambiguation: data.sessionState === 'disambiguation_required',
        isCollecting: data.sessionState === 'collection_in_progress',
        canStartCollection: data.sessionState === 'ready_for_collection',
        isError: Boolean(data.error),
        errorMessage: data.error || null,
        vectorProcessing: data.vectorProcessing || {},
        qualityMetrics: data.qualityMetrics || {},
        lastUpdated: data.lastUpdated || new Date().toISOString(),
        fetchedAt: new Date().toISOString()
      }
      
      console.log('Processed results with state:', {
        sessionState: processedResults.sessionState,
        hasResults: processedResults.hasResults,
        articlesCount: processedResults.articles.data?.length || 0,
        categoriesCount: Object.keys(processedResults.categoryBreakdown).length,
        nextSteps: processedResults.nextSteps
      })
      
      return processedResults
      
    } catch (err) {
      const status = err.response?.status
      const errorData = err.response?.data
      
      let errorMessage = 'Failed to get collection results'
      
      switch (status) {
        case 404:
          errorMessage = errorData?.message || 'Session not found or no results available'
          if (errorData?.sessionState) {
            console.log('Session exists but no results yet, returning empty state')
            const emptyResults = {
              sessionState: errorData.sessionState,
              nextSteps: errorData.nextSteps || [],
              articles: { data: [], pagination: null },
              categoryBreakdown: {},
              collectionMetadata: {},
              hasResults: false,
              needsDisambiguation: errorData.sessionState === 'disambiguation_required',
              canStartCollection: errorData.sessionState === 'ready_for_collection',
              isError: false,
              errorMessage: null,
              lastUpdated: new Date().toISOString(),
              fetchedAt: new Date().toISOString()
            }
            setCollectionResults(sessionId, emptyResults)
            return emptyResults
          }
          break
          
        case 400:
          if (errorData?.code === 'DATA_COLLECTION_NOT_FOUND' || 
              errorData?.error === 'Data collection not found' ||
              errorData?.message?.includes('Data collection not found')) {
            console.log('New session - data collection not started yet, returning initial state')
            const initialResults = {
              sessionState: 'ready_for_collection',
              nextSteps: ['Start data collection by selecting categories'],
              articles: { data: [], pagination: null },
              categoryBreakdown: {},
              collectionMetadata: {},
              hasResults: false,
              needsDisambiguation: false,
              canStartCollection: true,
              isCollecting: false,
              isComplete: false,
              isError: false,
              errorMessage: null,
              isNewSession: true,
              lastUpdated: new Date().toISOString(),
              fetchedAt: new Date().toISOString()
            }
            setCollectionResults(sessionId, initialResults)
            return initialResults
          }
          errorMessage = errorData?.message || errorData?.error || 'Bad request'
          break
          
        case 500:
          errorMessage = errorData?.message || 'Internal server error occurred'
          break
          
        case 403:
          errorMessage = 'Access denied to collection results'
          break
          
        case 401:
          errorMessage = 'Authentication required'
          break
          
        default:
          errorMessage = errorData?.message || err.message || errorMessage
      }
      
      console.error('Failed to fetch collection results:', {
        status,
        statusText: err.response?.statusText,
        url: err.config?.url,
        data: errorData,
        projectId,
        sessionId
      })
      
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  // Convenience methods for different result scenarios
  async function getCollectionResultsByState(projectId, sessionId, expectedState) {
    const results = await getLatestCollectionResults(projectId, sessionId)
    
    if (results.sessionState !== expectedState) {
      console.warn(`Expected state ${expectedState} but got ${results.sessionState}`)
    }
    
    return results
  }

  async function getCollectionResultsWithArticles(projectId, sessionId, limit = 50) {
    return getLatestCollectionResults(projectId, sessionId, { 
      includeArticles: true, 
      limit 
    })
  }

  async function getCategoryCollectionResults(projectId, sessionId, categoryId) {
    return getLatestCollectionResults(projectId, sessionId, { 
      categoryId,
      includeArticles: true 
    })
  }

  // State checker utilities
  function isSessionReady(sessionId) {
    const results = getCollectionResults(sessionId)
    return results?.canStartCollection || false
  }

  function hasCollectionResults(sessionId) {
    const results = getCollectionResults(sessionId)
    return results?.hasResults || false
  }

  function needsDisambiguation(sessionId) {
    const results = getCollectionResults(sessionId)
    return results?.needsDisambiguation || false
  }

  function isCollectionInProgress(sessionId) {
    const results = getCollectionResults(sessionId)
    return results?.isCollecting || false
  }

  function getSessionStateInfo(sessionId) {
    const results = getCollectionResults(sessionId)
    
    if (!results) {
      return {
        state: 'unknown',
        displayText: 'Loading...',
        canProceed: false,
        nextAction: null
      }
    }
    
    const stateMap = {
      'disambiguation_required': {
        displayText: 'Entity disambiguation needed',
        canProceed: false,
        nextAction: 'disambiguate'
      },
      'ready_for_collection': {
        displayText: results.isNewSession ? 'Ready to start data collection' : 'Ready to start data collection',
        canProceed: true,
        nextAction: 'start_collection',
        isNewSession: results.isNewSession || false
      },
      'collection_in_progress': {
        displayText: 'Data collection in progress',
        canProceed: false,
        nextAction: 'wait'
      },
      'completed': {
        displayText: 'Data collection completed',
        canProceed: true,
        nextAction: 'review_results'
      }
    }
    
    const stateInfo = stateMap[results.sessionState] || {
      displayText: 'Unknown state',
      canProceed: false,
      nextAction: null
    }
    
    return {
      state: results.sessionState,
      ...stateInfo,
      hasResults: results.hasResults,
      resultsCount: results.articles?.data?.length || 0,
      categoriesCount: Object.keys(results.categoryBreakdown || {}).length,
      lastUpdated: results.lastUpdated,
      isNewSession: results.isNewSession || false,
      shouldShowRetry: !results.isNewSession && results.isError
    }
  }

  // Category update methods (moved from research store)
  async function getCategoryUpdateStatus(projectId, sessionId) {
    if (!projectId || !sessionId) throw new Error('Project ID and Session ID are required')
    
    try {
      const res = await api.get(`/research/projects/${projectId}/sessions/${sessionId}/data-collection/category-status`)
      return res.data
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to get category status'
      console.error('Failed to fetch category status:', errorMessage)
      error.value = errorMessage
      throw new Error(errorMessage)
    }
  }

  async function previewCategoryUpdates(projectId, sessionId, selectedCategories) {
    if (!projectId || !sessionId) throw new Error('Project ID and Session ID are required')
    
    try {
      const res = await api.post(`/research/projects/${projectId}/sessions/${sessionId}/data-collection/update-preview`, {
        selectedCategories
      })
      return res.data
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to preview category updates'
      console.error('Failed to preview category updates:', errorMessage)
      error.value = errorMessage
      throw new Error(errorMessage)
    }
  }

  async function executeSelectiveUpdate(projectId, sessionId, selectedCategories, updateStrategy = 'merge') {
    if (!projectId || !sessionId) throw new Error('Project ID and Session ID are required')
    
    loading.value = true
    error.value = null
    
    try {
      const res = await api.post(`/research/projects/${projectId}/sessions/${sessionId}/data-collection/selective-update`, {
        selectedCategories,
        updateStrategy
      })
      
      if (res.data.results) {
        setCollectionResults(sessionId, res.data.results)
      }
      
      return res.data
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to execute selective update'
      console.error('Failed to execute selective update:', errorMessage)
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  // Automated data collection (unused but preserved)
  async function collectFromDataSource(projectId, payload) {
    if (!projectId) throw new Error('No project ID provided')
    
    try {
      const res = await api.post(`/research/projects/${projectId}/collect`, payload)
      return res.data
    } catch (error) {
      throw error
    }
  }

  return {
    // State
    loading,
    error,
    collectionResults,

    // Collection management
    setCollectionResults,
    getCollectionResults,
    clearCollectionResults,
    
    // Data collection operations
    getDataCollectionCategories,
    startDataCollection,
    getCollectionStatus,
    getSessionCollectionStatus,
    getLatestCollectionResults,
    getCollectionResultsByState,
    getCollectionResultsWithArticles,
    getCategoryCollectionResults,
    
    // State checkers
    isSessionReady,
    hasCollectionResults,
    needsDisambiguation,
    isCollectionInProgress,
    getSessionStateInfo,
    
    // Category updates
    getCategoryUpdateStatus,
    previewCategoryUpdates,
    executeSelectiveUpdate,
    
    // Automated collection (preserved)
    collectFromDataSource
  }
})